import { Box, CircularProgress, Typography } from '@mui/material';
import { SetStateAction, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import useHeader from '../Header/hooks/useHeader';
import { ReportItem } from './components/ReportItem';
import SelectCategoryReport from './components/SelectCategories/SelectCategoryReport';
import {
  DashboardI,
  GetDashboardsResponseDTO,
  GetEmbedUrlResponseDTO,
} from './declarations/reporting';
import { useFetchDashboardIds, useFetchEmbedUrl } from './hooks/useReporting';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { useTickets } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { ApiErrorI } from 'declarations/interfaces/errors';

const InsightsContent = () => {
  const [dashboards, setDashboards] = useState<DashboardI[]>();
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardI>({} as DashboardI);
  const [embedUrl, setEmbedUrl] = useState<GetEmbedUrlResponseDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [roofTopIds, setRoofTopIds] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const { roofTopsList } = useTickets();

  useEffect(() => {
    const rooftopid = searchParams.get(TicketsRefinementKeysE.RoofTopId);
    if (rooftopid) {
      setRoofTopIds([rooftopid.toUpperCase()]);
    } else {
      const list = roofTopsList ?? [];
      setRoofTopIds(list.map(roofTop => roofTop.id.toUpperCase()));
    }
  }, [roofTopsList, searchParams]);

  useHeader();
  useFetchDashboardIds({
    onSuccess: (data: GetDashboardsResponseDTO) => {
      const newDashboards = (data as DashboardI[]).map(dashboard => ({
        ...dashboard,
      }));
      const defaultDashboard = newDashboards[0];
      setDashboards(newDashboards);
      setSelectedDashboard(defaultDashboard);
      setLoading(false);
    },
    onError: (error: { code: SetStateAction<string | null> }) => {
      setLoading(false);
      setError(error.code);
    },
  });

  useFetchEmbedUrl({
    dashboardId: selectedDashboard.dashboardId,
    roofTopIds: roofTopIds,
    onSuccess: (data: any) => {
      setEmbedUrl(data);
      setLoading(false);
    },
    onError: (error: ApiErrorI) => {
      setLoading(false);
      if (error.data?.status === 403) {
        setError('You do not have access to this dashboard.');
      } else {
        setError(error.code);
      }
    },
  });

  const handleDashboardClick = (dashboard: DashboardI) => {
    setError(null);
    setSelectedDashboard(dashboard);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '90vh',
        overflowY: 'inherit',
      }}
    >
      <FlexContainer>
        <SelectCategoryReport
          dashboards={dashboards}
          value={selectedDashboard}
          onChange={handleDashboardClick}
          isLoading={loading}
        />
      </FlexContainer>
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <Typography variant="h5" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && embedUrl && (
          <Box sx={{ flexGrow: 1, height: '100%', display: 'flex' }}>
            <ReportItem
              id={embedUrl.dashboardId}
              route={embedUrl.embedURL ?? ''}
              roofTopIds={roofTopIds}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InsightsContent;
